import React, { useState, useEffect, createContext, useContext } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { CloudOff, Refresh } from '@mui/icons-material';

const ConnectionContext = createContext();

export const useConnection = () => useContext(ConnectionContext);

export function ConnectionProvider({ children }) {
  const [isOffline, setIsOffline] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const setConnectionError = (hasError) => {
    setIsOffline(hasError);
    if (hasError) {
      setRetryCount(prev => prev + 1);
    } else {
      setRetryCount(0);
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      // Try to reach the backend
      const response = await fetch(`${window.location.origin}/api/auth/csrf/`, {
        method: 'GET',
        credentials: 'include'
      });
      if (response.ok) {
        setIsOffline(false);
        setRetryCount(0);
        window.location.reload();
      }
    } catch (error) {
      console.log('Still offline...');
    } finally {
      setIsRetrying(false);
    }
  };

  // Auto-retry every 30 seconds when offline
  useEffect(() => {
    if (!isOffline) return;

    const interval = setInterval(() => {
      handleRetry();
    }, 30000);

    return () => clearInterval(interval);
  }, [isOffline]);

  return (
    <ConnectionContext.Provider value={{ isOffline, setConnectionError, retryCount }}>
      {isOffline ? (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            p: 3,
            textAlign: 'center'
          }}
        >
          <CloudOff sx={{ fontSize: 80, color: 'primary.main', mb: 3 }} />
          
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Site Updating
          </Typography>
          
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2, maxWidth: 500 }}>
            The site is currently being updated and should be back online in a few minutes.
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            We're making improvements! Please wait a moment.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            {isRetrying ? (
              <>
                <CircularProgress size={24} />
                <Typography color="text.secondary">Checking connection...</Typography>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={handleRetry}
                size="large"
              >
                Try Again
              </Button>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">
            Auto-retrying every 30 seconds... (Attempt #{retryCount})
          </Typography>
        </Box>
      ) : (
        children
      )}
    </ConnectionContext.Provider>
  );
}

export default ConnectionProvider;